import type {
  AnalysisFilters,
  AnalysisStatus,
  CreativeAiAnalysis,
  CreativeAiAnalysisDetail,
  CreativeAiAnalysisListResponse,
  CreativeKind,
} from './analysis.types'
import type {
  CreativeLabellingAnalysisProgress,
  CreativeLabellingProgressEvent,
  CreativeLabellingRun,
  InsightStatus,
  LabellingRunListResponse,
  LabellingRunType,
  LabellingStatus,
} from './labelling.types'
import type {
  CreativeLabelDefinition,
  CreativeLabelOption,
  CreativeLabelSchema,
  LabelSchemaMeta,
  LabelSchemaResponse,
  LabelSchemaRunSummary,
} from './labelSchema.types'

const ANALYSIS_STATUSES = new Set<AnalysisStatus>([
  'pending',
  'labelling',
  'analysing',
  'cancelling',
  'done',
  'failed',
  'cancelled',
])
const LABELLING_STATUSES = new Set<LabellingStatus>([
  'pending',
  'running',
  'cancel_requested',
  'completed',
  'failed',
  'cancelled',
])
const RUN_TYPES = new Set<LabellingRunType>(['initial', 'relabel', 'manual'])
const INSIGHT_STATUSES = new Set<InsightStatus>([
  'queued',
  'running',
  'completed',
  'failed',
  'partial',
])

function record(value: unknown, name: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`)
  }
  return value as Record<string, unknown>
}

function stringValue(value: unknown, name: string): string {
  if (typeof value !== 'string') throw new TypeError(`${name} must be a string`)
  return value
}

function numberValue(value: unknown, name: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new TypeError(`${name} must be a finite number`)
  }
  return value
}

function booleanValue(value: unknown, name: string): boolean {
  if (typeof value !== 'boolean') throw new TypeError(`${name} must be a boolean`)
  return value
}

function nullableString(value: unknown, name: string): string | null {
  return value === null ? null : stringValue(value, name)
}

function nullableNumber(value: unknown, name: string): number | null {
  return value === null ? null : numberValue(value, name)
}

function enumValue<T extends string>(value: unknown, values: ReadonlySet<T>, name: string): T {
  const candidate = stringValue(value, name) as T
  if (!values.has(candidate)) throw new TypeError(`${name} has an unsupported value`)
  return candidate
}

function creativeKind(value: unknown, name: string): CreativeKind {
  if (value !== 'image' && value !== 'video') throw new TypeError(`${name} must be image or video`)
  return value
}

function analysisFilters(value: unknown): AnalysisFilters {
  if (Array.isArray(value)) {
    for (const [index, item] of value.entries()) {
      stringValue(record(item, `filters[${index}]`).field, `filters[${index}].field`)
    }
    return value as AnalysisFilters
  }
  return record(value, 'filters')
}

export function decodeCreativeAiAnalysis(value: unknown): CreativeAiAnalysis {
  const row = record(value, 'analysis')
  const selectedLabels = row.selected_labels
  if (!Array.isArray(selectedLabels) || !selectedLabels.every((item) => typeof item === 'string')) {
    throw new TypeError('analysis.selected_labels must be a string array')
  }
  const analysisType = stringValue(row.analysis_type, 'analysis.analysis_type')
  if (!['overall', 'launch', 'combination'].includes(analysisType)) {
    throw new TypeError('analysis.analysis_type has an unsupported value')
  }
  return {
    id: numberValue(row.id, 'analysis.id'),
    workspace: numberValue(row.workspace, 'analysis.workspace'),
    creator: nullableNumber(row.creator, 'analysis.creator'),
    labeling_run: nullableNumber(row.labeling_run, 'analysis.labeling_run'),
    status: enumValue(row.status, ANALYSIS_STATUSES, 'analysis.status'),
    total_count: numberValue(row.total_count, 'analysis.total_count'),
    labelled_count: numberValue(row.labelled_count, 'analysis.labelled_count'),
    skipped_count: numberValue(row.skipped_count, 'analysis.skipped_count'),
    failed_count: numberValue(row.failed_count, 'analysis.failed_count'),
    // Added to the serializer after BE-23-01 (v3 ORP-20375): absent on older rows, so read leniently.
    credit_exhausted: row.credit_exhausted === true,
    untried_count: Number.isFinite(row.untried_count) ? (row.untried_count as number) : 0,
    failure_reason: nullableString(row.failure_reason, 'analysis.failure_reason'),
    is_stale: booleanValue(row.is_stale, 'analysis.is_stale'),
    analyzed_image_count: numberValue(row.analyzed_image_count, 'analysis.analyzed_image_count'),
    analyzed_video_count: numberValue(row.analyzed_video_count, 'analysis.analyzed_video_count'),
    name: stringValue(row.name, 'analysis.name'),
    creative_type: creativeKind(row.creative_type, 'analysis.creative_type'),
    analysis_type: analysisType as CreativeAiAnalysis['analysis_type'],
    date_from: stringValue(row.date_from, 'analysis.date_from'),
    date_to: stringValue(row.date_to, 'analysis.date_to'),
    filters: analysisFilters(row.filters),
    selected_labels: selectedLabels,
    created_at: stringValue(row.created_at, 'analysis.created_at'),
    updated_at: stringValue(row.updated_at, 'analysis.updated_at'),
  }
}

export function decodeCreativeAiAnalysisDetail(value: unknown): CreativeAiAnalysisDetail {
  const row = record(value, 'analysis detail')
  return {
    ...decodeCreativeAiAnalysis(row),
    brief_segment_count: numberValue(row.brief_segment_count, 'analysis.brief_segment_count'),
    brief_credit_rate: numberValue(row.brief_credit_rate, 'analysis.brief_credit_rate'),
  }
}

export function decodeCreativeAiAnalysisList(value: unknown): CreativeAiAnalysisListResponse {
  const row = record(value, 'analysis list')
  if (!Array.isArray(row.analyses)) throw new TypeError('analysis list.analyses must be an array')
  return {
    analyses: row.analyses.map(decodeCreativeAiAnalysis),
    image_label_count: numberValue(row.image_label_count, 'analysis list.image_label_count'),
    video_label_count: numberValue(row.video_label_count, 'analysis list.video_label_count'),
  }
}

function decodeRunSummary(value: unknown, name: string): LabelSchemaRunSummary {
  const row = record(value, name)
  return {
    id: numberValue(row.id, `${name}.id`),
    run_type: enumValue(row.run_type, RUN_TYPES, `${name}.run_type`),
    scope: creativeKind(row.scope, `${name}.scope`),
    status: enumValue(row.status, LABELLING_STATUSES, `${name}.status`),
    processed_count: numberValue(row.processed_count, `${name}.processed_count`),
    total_count: numberValue(row.total_count, `${name}.total_count`),
    failed_count: numberValue(row.failed_count, `${name}.failed_count`),
    skipped_count: numberValue(row.skipped_count, `${name}.skipped_count`),
    eta_seconds: nullableNumber(row.eta_seconds, `${name}.eta_seconds`),
    started_at: nullableString(row.started_at, `${name}.started_at`),
    finished_at: nullableString(row.finished_at, `${name}.finished_at`),
    error_message: nullableString(row.error_message, `${name}.error_message`),
  }
}

function decodeLabelOption(value: unknown, name: string): CreativeLabelOption {
  const row = record(value, name)
  const conflict = row.on_labelled_conflict
  if (conflict !== undefined && conflict !== 'keep' && conflict !== 'change') {
    throw new TypeError(`${name}.on_labelled_conflict has an unsupported value`)
  }
  return {
    value: stringValue(row.value, `${name}.value`),
    display_name: stringValue(row.display_name, `${name}.display_name`),
    description: nullableString(row.description, `${name}.description`),
    ...(conflict === undefined ? {} : { on_labelled_conflict: conflict }),
  }
}

function decodeLabelDisplay(
  value: unknown,
  name: string,
): NonNullable<CreativeLabelDefinition['display']> {
  const row = record(value, name)
  const output = { ...row }
  if (row.true_text !== undefined)
    output.true_text = stringValue(row.true_text, `${name}.true_text`)
  if (row.false_text !== undefined) {
    output.false_text = stringValue(row.false_text, `${name}.false_text`)
  }
  if (row.values !== undefined) {
    output.values = Object.fromEntries(
      Object.entries(record(row.values, `${name}.values`)).map(([key, item]) => [
        key,
        stringValue(item, `${name}.values.${key}`),
      ]),
    )
  }
  return output
}

function decodeLabelSchema(value: unknown, name: string): CreativeLabelSchema {
  const source = record(value, name)
  return Object.fromEntries(
    Object.entries(source).map(([key, item]) => {
      const row = record(item, `${name}.${key}`)
      const definition: CreativeLabelDefinition = {
        ...row,
        name: stringValue(row.name, `${name}.${key}.name`),
        type: stringValue(row.type, `${name}.${key}.type`),
      }
      if (row.description !== undefined) {
        definition.description = stringValue(row.description, `${name}.${key}.description`)
      }
      if (row.display !== undefined) {
        definition.display = decodeLabelDisplay(row.display, `${name}.${key}.display`)
      }
      if (row.values !== undefined) {
        if (!Array.isArray(row.values))
          throw new TypeError(`${name}.${key}.values must be an array`)
        definition.values = row.values.map((option, index) =>
          decodeLabelOption(option, `${name}.${key}.values[${index}]`),
        )
      }
      if (row.enum !== undefined) {
        if (!Array.isArray(row.enum)) throw new TypeError(`${name}.${key}.enum must be an array`)
        definition.enum = row.enum.map((option, index) =>
          decodeLabelOption(option, `${name}.${key}.enum[${index}]`),
        )
      }
      return [key, definition]
    }),
  )
}

export function decodeLabelSchemaResponse(value: unknown): LabelSchemaResponse {
  const row = record(value, 'label schema')
  return {
    image_schema: decodeLabelSchema(row.image_schema, 'label schema.image_schema'),
    video_schema: decodeLabelSchema(row.video_schema, 'label schema.video_schema'),
    ad_schema: decodeLabelSchema(row.ad_schema, 'label schema.ad_schema'),
    label_count: numberValue(row.label_count, 'label schema.label_count'),
    video_label_count: numberValue(row.video_label_count, 'label schema.video_label_count'),
    updated_at: nullableString(row.updated_at, 'label schema.updated_at'),
    video_readonly: booleanValue(row.video_readonly, 'label schema.video_readonly'),
    ad_readonly: booleanValue(row.ad_readonly, 'label schema.ad_readonly'),
    active_run:
      row.active_run === null ? null : decodeRunSummary(row.active_run, 'label schema.active_run'),
    last_completed_run:
      row.last_completed_run === null
        ? null
        : decodeRunSummary(row.last_completed_run, 'label schema.last_completed_run'),
  }
}

export function decodeLabelSchemaMeta(value: unknown): LabelSchemaMeta {
  const row = record(value, 'label schema meta')
  const constraints = record(row.constraints, 'label schema meta.constraints')
  if (!Array.isArray(row.types) || !Array.isArray(row.scopes)) {
    throw new TypeError('label schema meta options must be arrays')
  }
  return {
    constraints: {
      max_labels_per_schema: numberValue(
        constraints.max_labels_per_schema,
        'constraints.max_labels_per_schema',
      ),
      max_labels_per_scope: Object.fromEntries(
        Object.entries(
          record(constraints.max_labels_per_scope, 'constraints.max_labels_per_scope'),
        ).map(([key, count]) => [
          key,
          numberValue(count, `constraints.max_labels_per_scope.${key}`),
        ]),
      ),
      multiple_min_values: numberValue(
        constraints.multiple_min_values,
        'constraints.multiple_min_values',
      ),
      reserved_key_prefix: stringValue(
        constraints.reserved_key_prefix,
        'constraints.reserved_key_prefix',
      ),
      key_pattern: stringValue(constraints.key_pattern, 'constraints.key_pattern'),
    },
    types: row.types.map((item, index) => {
      const option = record(item, `label schema meta.types[${index}]`)
      return {
        value: stringValue(option.value, `label schema meta.types[${index}].value`),
        label: stringValue(option.label, `label schema meta.types[${index}].label`),
      }
    }),
    scopes: row.scopes.map((item, index) => {
      const option = record(item, `label schema meta.scopes[${index}]`)
      return {
        value: stringValue(option.value, `label schema meta.scopes[${index}].value`),
        label: stringValue(option.label, `label schema meta.scopes[${index}].label`),
        readonly: booleanValue(option.readonly, `label schema meta.scopes[${index}].readonly`),
      }
    }),
  }
}

export function decodeCreativeLabellingRun(value: unknown): CreativeLabellingRun {
  const row = record(value, 'labelling run')
  if (!Array.isArray(row.failed_sample)) {
    throw new TypeError('labelling run.failed_sample must be an array')
  }
  return {
    ...decodeRunSummary(row, 'labelling run'),
    workspace_id: numberValue(row.workspace_id, 'labelling run.workspace_id'),
    display_status: enumValue(
      row.display_status,
      ANALYSIS_STATUSES,
      'labelling run.display_status',
    ),
    failed_sample: row.failed_sample.map((item, index) => record(item, `failed_sample[${index}]`)),
    schema_diff: record(row.schema_diff, 'labelling run.schema_diff'),
    triggered_by: nullableNumber(row.triggered_by, 'labelling run.triggered_by'),
    celery_task_id: nullableString(row.celery_task_id, 'labelling run.celery_task_id'),
    created_at: stringValue(row.created_at, 'labelling run.created_at'),
    updated_at: stringValue(row.updated_at, 'labelling run.updated_at'),
  }
}

export function decodeLabellingRunList(value: unknown): LabellingRunListResponse {
  const row = record(value, 'labelling run list')
  if (!Array.isArray(row.runs)) throw new TypeError('labelling run list.runs must be an array')
  return {
    runs: row.runs.map(decodeCreativeLabellingRun),
    active_run: row.active_run === null ? null : decodeCreativeLabellingRun(row.active_run),
  }
}

function decodeAnalysisProgress(value: unknown, index: number): CreativeLabellingAnalysisProgress {
  const row = record(value, `progress.analyses[${index}]`)
  const insight = (field: 'insight_status' | 'combination_insight_status'): InsightStatus | null =>
    row[field] === null ? null : enumValue(row[field], INSIGHT_STATUSES, `progress.${field}`)
  return {
    id: numberValue(row.id, 'progress analysis.id'),
    status: enumValue(row.status, ANALYSIS_STATUSES, 'progress analysis.status'),
    insight_status: insight('insight_status'),
    combination_insight_status: insight('combination_insight_status'),
  }
}

export function decodeCreativeLabellingProgress(value: unknown): CreativeLabellingProgressEvent {
  const row = record(value, 'creative labelling progress')
  if (row.event !== 'creative_labelling_progress' || !Array.isArray(row.analyses)) {
    throw new TypeError('creative labelling progress has an invalid shape')
  }
  const eventId = row.event_id
  if (typeof eventId !== 'string') {
    throw new TypeError('creative labelling progress.event_id must be a string')
  }
  const runType = enumValue(row.run_type, RUN_TYPES, 'progress.run_type')
  return {
    event: 'creative_labelling_progress',
    workspace_id: numberValue(row.workspace_id, 'progress.workspace_id'),
    ts: numberValue(row.ts, 'progress.ts'),
    ts_iso: stringValue(row.ts_iso, 'progress.ts_iso'),
    account_id: nullableNumber(row.account_id, 'progress.account_id'),
    event_id: eventId,
    id: numberValue(row.id, 'progress.id'),
    run_type: runType,
    scope: creativeKind(row.scope, 'progress.scope'),
    status:
      runType === 'relabel'
        ? enumValue(row.status, LABELLING_STATUSES, 'progress.status')
        : enumValue(row.status, ANALYSIS_STATUSES, 'progress.status'),
    labelled_count: numberValue(row.labelled_count, 'progress.labelled_count'),
    processed_count: numberValue(row.processed_count, 'progress.processed_count'),
    total_count: numberValue(row.total_count, 'progress.total_count'),
    skipped_count: numberValue(row.skipped_count, 'progress.skipped_count'),
    failed_count: numberValue(row.failed_count, 'progress.failed_count'),
    eta_seconds: nullableNumber(row.eta_seconds, 'progress.eta_seconds'),
    started_at: nullableString(row.started_at, 'progress.started_at'),
    finished_at: nullableString(row.finished_at, 'progress.finished_at'),
    error_message: nullableString(row.error_message, 'progress.error_message'),
    analyses: row.analyses.map(decodeAnalysisProgress),
  }
}
